import LinkOffIcon from "@mui/icons-material/LinkOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCustomisation } from "../../context/CustomisationContext";
import { useToast } from "../../context/ToastContext";
import { useTranslate } from "../../hooks/useTranslate";
import { getUserPool, reInviteUser } from "../../services/api/apiClient";
import { LoginFlow } from "../../services/api/types/auth/userPool";
import { FooterLogo } from "../components/FooterLogo";


export const LinkExpired: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { customisations } = useCustomisation();
  const t = useTranslate("link-expired");
  const footerText = useTranslate("login")("footer","poweredBy")

  const { userName } = (location.state as { userName?: string }) ?? {};

  const {
    LOGIN: {
      buttonsSettings: loginbuttonssettings,
      textColorsSettings: loginTextColors,
      backgroundSettings: loginBgSettings,
      logoSettings: loginLogoSettings,
    },
    GPC: {
      buttonsSettings: gpcbuttonssettings,
      textColorsSettings: gpcTextColors,
      backgroundSettings: gpcBgSettings,
      logoSettings: gpcLogoSettings,
    },
  } = customisations.customisations;

  const primaryBgColor =
    loginBgSettings?.loginPrimaryBgColor ?? gpcBgSettings?.gpcPrimaryBgColor;
  const secondaryBgColor =
    loginBgSettings?.loginSecondaryBgColor ?? gpcBgSettings?.gpcSecondaryBgColor;
  const logo =
    loginLogoSettings?.loginLandscapeLogo ?? gpcLogoSettings?.gpcLandscapeLogo;
  const secondaryTextColor =
    loginTextColors?.loginSecondaryTextColor ?? gpcTextColors?.gpcSecondaryTextColor;
  const primaryButtonBg =
    loginbuttonssettings?.loginBtnPrimaryBgColor ?? gpcbuttonssettings?.gpcBtnPrimaryBgColor;
  const primaryButtonText =
    loginbuttonssettings?.loginBtnPrimaryTextColor ?? gpcbuttonssettings?.gpcBtnPrimaryTextColor;

  const handleReInvite = async () => {
    try {
      if (!userName) return;
      setLoading(true);
      const userPool = await getUserPool({
        email: userName,
        loginFlow: LoginFlow.FIRST_TIME_LOGIN,
        portalUrl: window.location.origin,
      });
      if (userPool.poolId) {
        await reInviteUser({
          email: userName,
          confirmationCode: userPool.poolId.split("_").pop()!,
        });
        showToast("Invitation link has been resent", "success");
        navigate("/login");
      }
    } catch (error: any) {
      console.log("Error: ", error);
      showToast(
        error?.response?.data ?? "Something went wrong",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        py: 6,
        bgcolor: primaryBgColor,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Container maxWidth="lg">
          <Stack alignItems="center" spacing={4}>
            <Paper
              elevation={1}
              sx={{
                maxWidth: 440,
                width: "100%",
                p: 4,
                borderRadius: 2,
                bgcolor: secondaryBgColor,
              }}
            >
              <Stack spacing={3} width="100%" alignItems="center">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 50,
                    maxWidth: 200,
                  }}
                >
                  <Box
                    component="img"
                    src={`/${logo}`}
                    alt="logo"
                    sx={{
                      height: 50,
                      maxWidth: 200,
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "#FEF3C7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LinkOffIcon sx={{ color: "#D97706", fontSize: 28 }} />
                </Box>

                <Stack spacing={1} width="100%" alignItems="center">
                  <Typography
                    variant="h5"
                    align="center"
                    color={secondaryTextColor}
                    sx={{ fontWeight: 700 }}
                  >
                    {t("linkExpiredHeader", "linkExpiredText")}
                  </Typography>
                  <Typography variant="body2" align="center" color={secondaryTextColor}>
                    {t("linkExpiredHeader", "linkExpiredDsc")}
                  </Typography>
                </Stack>

                <Stack spacing={1.5} width="100%">
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleReInvite}
                    disabled={loading}
                    sx={{
                      bgcolor: primaryButtonBg,
                      color: primaryButtonText,
                      fontWeight: 600,
                      borderRadius: "8px",
                      py: 1.25,
                      "&:hover": {
                        bgcolor: primaryButtonBg,
                        opacity: 0.9,
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      t("linkExpiredForm", "btnResendInvitation")
                    )}
                  </Button>

                  <Button
                    component={Link}
                    to="/login"
                    fullWidth
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      color: secondaryTextColor,
                      borderColor: "rgba(0,0,0,0.23)",
                      fontWeight: 600,
                      borderRadius: "8px",
                      py: 1.25,
                      "&:hover": {
                        borderColor: "rgba(0,0,0,0.4)",
                        bgcolor: "transparent",
                      },
                    }}
                  >
                    {t("linkExpiredForm", "btnBackToLogin")}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
      <FooterLogo poweredByText={footerText} />
    </Box>
  );
};
